import { Button, CircularProgress, Dialog, DialogContent, DialogTitle, DialogActions, IconButton, FormHelperText, AlertTitle } from '@mui/material'
import React from 'react'
import { useIntl } from 'react-intl'
import { useFormik } from 'formik'
import { Box } from '@mui/material'
import CustomInput from '../../../../../components/muiComponents/customInput'
import { H6 } from '../../../../../layouts/TextStyles'
import DeleteIcon from '@mui/icons-material/Delete'
import FileUploadIcon from '@mui/icons-material/FileUpload'
import DateTimePicker from '../../../../../components/muiComponents/timePicker/TimePicker'
import { HomeworkState, File as ServerFile } from '../../../../../features/types'
import { Moment } from 'moment'
import dayjs from 'dayjs'
import { toast } from 'react-toastify'
import { homeworkApiSlice } from '../../../../../features/section/homeworkSlice'
import { isRequiredDate, requiredString } from '../../../../../utils/validation'
import { requiredId } from '../../../../../utils/validation'
import * as yup from 'yup'
import { tone, ui } from '../../../../../utils/uiTokens'


interface Props {
    editHomework?: HomeworkState | null
    setEditHomework?: (element: any) => void
    courseTopicId: number
    refetchHomework?: () => void
}

const AddHomeworkDialog = ({ editHomework, setEditHomework = () => { }, courseTopicId, refetchHomework = () => { } }: Props) => {
    const [open, setOpen] = React.useState(false)
    const { formatMessage } = useIntl()
    const [files, setFiles] = React.useState<File[]>([])
    const [existingFiles, setExistingFiles] = React.useState<ServerFile[]>([])

    React.useEffect(() => {
        setFiles([])
        if (editHomework) {
            setOpen(true)
            setExistingFiles(editHomework.files || [])
        } else {
            formik.resetForm()
            setExistingFiles([])
        }
    }, [editHomework])

    const handleClose = () => {
        setOpen(false)
        setFiles([])
        setExistingFiles([])
        formik.resetForm()
        setEditHomework(null)
    }

    const handleClickOpen = () => {
        setOpen(true)
        formik.resetForm()
        setFiles([])
        setExistingFiles([])
    }

    const [createHomework, { isLoading: isCreating }] =
        homeworkApiSlice.useCreateHomeworkMutation()

    const [updateHomework, { isLoading: isUpdating }] =
        homeworkApiSlice.useUpdateHomeworkMutation()
    const [deleteHomeworkFile, { isLoading: isDeletingFile }] =
        homeworkApiSlice.useDeleteHomeworkFileMutation()

    // Функция для проверки, был ли файл удален
    const isFileDeleted = (originalFile: ServerFile) =>
        !existingFiles.some(currentFile => currentFile.id === originalFile.id)

    // Функция для создания FormData с базовыми полями
    const createFormData = (title: string, description: string, startDateTime: string | null, endDateTime: string | null) => {
        const formData = new FormData()
        formData.append('courseTopicId', courseTopicId.toString())
        formData.append('title', title)
        formData.append('description', description)
        formData.append('startDateTime', startDateTime || '')
        formData.append('endDateTime', endDateTime || '')
        files.forEach((file) => {
            formData.append('files', file)
        })

        return formData
    }

    const validationSchema = yup.object().shape({
        courseTopicId: requiredId({ formatMessage }),
        title: requiredString({ formatMessage }),
        description: requiredString({ formatMessage }),
        files: yup.array().of(yup.mixed<File>()).optional(),
        startDateTime: isRequiredDate({ formatMessage }),
        endDateTime: isRequiredDate({ formatMessage }),
    })

    const formik = useFormik({
        enableReinitialize: true,
        initialValues: {
            id: editHomework?.id || 0,
            courseTopicId: editHomework?.courseTopicId ?? courseTopicId,
            title: editHomework?.title ?? '',
            description: editHomework?.description ?? '',
            files: [],
            startDateTime: editHomework?.startDateTime ?? null,
            endDateTime: editHomework?.endDateTime ?? null,
        },
        validationSchema: validationSchema,
        validateOnChange: false,
        validateOnBlur: false,

        onSubmit: async () => {
            if (editHomework) {
                handleUpdateHomework()
            } else {
                handleCreateHomework()
            }
        },
    })
    const handleCreateHomework = async () => {
        try {
            if (!validateFileSize()) return

            const { title, description, startDateTime, endDateTime } = formik.values as HomeworkState

            const formData = createFormData(title, description, startDateTime, endDateTime)

            await createHomework({
                courseTopicId,
                body: formData,
            }).unwrap()
            toast.success(formatMessage({ id: 'savedSuccess' }))
            refetchHomework()
            handleClose()
        } catch (error) {
            toast.error(formatMessage({ id: 'addFail' }))
        }
    }

    const handleUpdateHomework = async () => {
        try {
            if (!validateFileSize()) return

            const { title, description, startDateTime, endDateTime } = formik.values as HomeworkState

            const originalFiles = editHomework?.files || []
            const deletedFiles = originalFiles.filter(isFileDeleted)

            for (const deletedFile of deletedFiles) {
                await deleteHomeworkFile(deletedFile.id).unwrap()
            }

            const formData = createFormData(title, description, startDateTime, endDateTime)

            await updateHomework({
                id: editHomework.id,
                body: formData,
            }).unwrap()
            toast.success(formatMessage({ id: 'updatedSuccess' }))
            refetchHomework()
            handleClose()
        } catch (error) {
            toast.error(formatMessage({ id: 'updateFail' }))
        }
    }

    const onFilesChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        if (e.target.files) {
            const fileList = Array.from(e.target.files)
            formik.setFieldValue('files', fileList)
            setFiles(fileList)
        }
    }

    const handleDeleteFile = (fileToDelete: File) => {
        const updatedFiles = files.filter(file => file !== fileToDelete)
        setFiles(updatedFiles)
        formik.setFieldValue('files', updatedFiles)
    }

    const handleDeleteExistingFile = (fileToDelete: ServerFile) => {
        if (!fileToDelete || typeof fileToDelete.id !== 'number') {
            return
        }
        const filteredFiles = existingFiles?.filter(file => file?.id !== fileToDelete?.id)
        setExistingFiles(filteredFiles)
    }

    const handleDateChange = (key: 'startDateTime' | 'endDateTime', event: Moment) => {
        // Стёртое поле приходит как null, недописанная дата - как невалидный moment
        if (!event) return formik.setFieldValue(key, null)
        if (!event.isValid()) return
        formik.setFieldValue(
            key,
            dayjs(event.toDate()).format('YYYY-MM-DDTHH:mm:ss'),
        )
    }


    // Общая функция для проверки размера файлов
    const validateFileSize = (): boolean => {
        if (files.length === 0) return true // Если файлов нет, проверка не нужна
        const maxFileSize = 2 * 1024 * 1024 // 2MB
        const totalFileSize = files.reduce((total, file) => total + file.size, 0)
        if (totalFileSize > maxFileSize) {
            toast.error(formatMessage({ id: 'filesDescription' }))
            return false
        }
        return true
    }


    return (
        <div>
            <Button
                variant={'contained'}
                onClick={handleClickOpen}
            >
                {formatMessage({ id: 'addHomework' })}
            </Button>
            <Dialog
                maxWidth={'sm'}
                fullWidth
                open={open}
                onClose={handleClose}
            >
                <DialogTitle>
                    {editHomework
                        ? formatMessage({ id: 'edit' })
                        : formatMessage({ id: 'addHomework' })}
                </DialogTitle>


                <DialogContent>
                    <form onSubmit={formik.handleSubmit}>
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                            <Box sx={{ display: 'flex', gap: 2 }}>
                                <Box sx={{ width: '100%' }}>
                                    <H6>{formatMessage({ id: 'title' })}</H6>
                                    <CustomInput
                                        fullWidth
                                        id="title"
                                        name="title"
                                        value={formik.values.title}
                                        onChange={formik.handleChange}
                                        onBlur={formik.handleBlur}
                                        placeholder={formatMessage({ id: 'title' })}
                                    />
                                </Box>
                                <Box sx={{ width: '100%' }}>
                                    <H6>{formatMessage({ id: 'description' })}</H6>
                                    <CustomInput
                                        fullWidth
                                        id="description"
                                        name="description"
                                        value={formik.values.description}
                                        onChange={formik.handleChange}
                                        onBlur={formik.handleBlur}
                                        placeholder={formatMessage({ id: 'description' })}
                                    />
                                </Box>
                            </Box>

                            <Box sx={{ width: '100%' }}>
                                <Box sx={{ display: 'flex', alignItems: 'flex-end', gap: 3 }}>
                                    <H6>{formatMessage({ id: 'files' })}</H6>
                                    <AlertTitle sx={{ fontSize: '14px', m: 0, p: 0, color: tone.bad.text, textAlign: 'center', }}>{formatMessage({ id: 'filesDescription' })}</AlertTitle>
                                </Box>
                                <Box>
                                    <Button
                                        sx={{ mt: 2, mb: 1 }}
                                        component="label"
                                        variant="outlined"
                                        color="primary"
                                        startIcon={<FileUploadIcon />}
                                    >
                                        {formatMessage({ id: 'uploadFiles' })}
                                        <input
                                            type="file"
                                            hidden
                                            multiple
                                            accept=".pdf,.png,.jpg,.jpeg"
                                            onChange={onFilesChange}
                                        />
                                    </Button>

                                    <FormHelperText style={{ color: tone.bad.text, marginLeft: '12px' }}>
                                        {formik.touched.files && formik.errors.files ? String(formik.errors.files) : ''}
                                    </FormHelperText>

                                    {existingFiles.length > 0 && (
                                        <Box sx={{ display: 'flex', flexDirection: 'column', mt: 2 }}>
                                            <H6 style={{ fontSize: '14px', color: ui.sub, marginBottom: '8px' }}>
                                                {formatMessage({ id: 'existingFiles' }) || 'Существующие файлы:'}
                                            </H6>
                                            {existingFiles.map((file, index) => (
                                                <Box key={`existing-${index}`} sx={{ display: 'flex', alignItems: 'center', gap: 1, p: 1, bgcolor: ui.surface, borderRadius: 1 }}>
                                                    <FileUploadIcon color="secondary" />
                                                    <span style={{ flex: 1, fontSize: '14px' }}>
                                                        {file.title}
                                                    </span>
                                                    <IconButton
                                                        onClick={() => handleDeleteExistingFile(file)}
                                                        size="small"
                                                        color="error"
                                                    >
                                                        <DeleteIcon />
                                                    </IconButton>
                                                </Box>
                                            ))}
                                        </Box>
                                    )}

                                    {files.length > 0 && (
                                        <Box sx={{ display: 'flex', flexDirection: 'column', mt: 2 }}>
                                            <H6 style={{ fontSize: '14px', color: ui.sub, marginBottom: '8px' }}>
                                                {formatMessage({ id: 'newFiles' }) || 'Новые файлы:'}
                                            </H6>
                                            {files.map((file, index) => (
                                                <Box key={`new-${index}`} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                    <FileUploadIcon color="primary" />
                                                    <span style={{ flex: 0.8 }}>
                                                        {file.name}
                                                    </span>
                                                    <span style={{ flex: 0.2 }}>
                                                        {(file.size / 1024).toFixed(2)} KB
                                                    </span>
                                                    <IconButton
                                                        onClick={() => handleDeleteFile(file)}
                                                        size="small"
                                                        color="error"
                                                    >
                                                        <DeleteIcon />
                                                    </IconButton>
                                                </Box>
                                            ))}
                                        </Box>
                                    )}
                                </Box>
                            </Box>
                            <Box >
                                <H6>{formatMessage({ id: 'start&endTime' })}</H6>
                                <Box sx={{ display: 'flex', mt: 2 }}>
                                    <Box sx={{ mr: 2 }}>
                                        <DateTimePicker
                                            label={formatMessage({ id: 'startDateTime' })}
                                            value={formik.values.startDateTime}
                                            handleChange={(event) =>
                                                handleDateChange('startDateTime', event)
                                            }
                                        />
                                    </Box>
                                    <Box>
                                        <DateTimePicker
                                            label={formatMessage({ id: 'endDateTime' })}
                                            value={formik.values.endDateTime}
                                            handleChange={(event) =>
                                                handleDateChange('endDateTime', event)
                                            }
                                        />
                                    </Box>
                                </Box>
                            </Box>

                        </Box>
                    </form>
                </DialogContent>

                <DialogActions sx={{ m: 2, mt: 0 }}>
                    <Button
                        onClick={handleClose}
                        variant={'outlined'}
                        sx={{ mr: 1 }}
                        disabled={isCreating || isUpdating}
                    >
                        {formatMessage({ id: 'cancel' })}
                    </Button>

                    <Button
                        variant={'contained'}
                        sx={{ width: '120px' }}
                        disabled={isCreating || isUpdating}
                        onClick={() => formik.handleSubmit()}
                    >
                        {(isCreating || isUpdating) ? (
                            <CircularProgress size={20} />
                        ) : editHomework ? (
                            formatMessage({ id: 'update' })
                        ) : (
                            formatMessage({ id: 'save' })
                        )}
                    </Button>
                </DialogActions>
            </Dialog>
        </div>
    )
}

export default AddHomeworkDialog
